import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { PackageCards } from "@/components/PackageCards";
import { PageLoader } from "@/components/ProtectedRoute";
import { LuxuryHero } from "@/components/landing/LuxuryHero";

const Stats = ({ s }) => {
  const { tr } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12" data-testid="stats-section">
      <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3">
        {(s.stats || []).map((st, i) => (
          <div key={st.id || st.value} className="bg-card p-8" data-testid={`stat-item-${i}`}>
            <p className="text-4xl font-extrabold tracking-tight text-primary">{st.value}</p>
            <p className="mt-1 text-sm font-semibold uppercase tracking-widest text-muted-foreground">{tr(st.label)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const About = ({ s }) => {
  const { t, tr } = useLang();
  return (
    <section id="about" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6 lg:px-12 lg:py-24" data-testid="about-section">
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">{t("about_eyebrow")}</p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("about_title")}</h2>
        </div>
        <p className="whitespace-pre-line text-base leading-relaxed text-muted-foreground md:text-lg lg:col-span-8" data-testid="about-text">{tr(s.about_text)}</p>
      </div>
    </section>
  );
};

export default function Landing() {
  const { t } = useLang();
  const { settings: s } = useSettings();
  const { hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hash && s) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [hash, s]);

  if (!s) return <PageLoader />;
  const sec = s.sections || {};
  return (
    <div className="pb-16">
      {sec.hero && <LuxuryHero s={s} />}
      {sec.stats && <div className="pt-12"><Stats s={s} /></div>}
      {sec.about && <About s={s} />}
      {sec.packages && (
        <section id="packages" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-8 sm:px-6 lg:px-12" data-testid="packages-section">
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400">{t("packages_eyebrow")}</p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("packages_title")}</h2>
          <p className="mb-10 mt-3 max-w-2xl text-muted-foreground">{t("packages_subtitle")}</p>
          <PackageCards packages={s.packages} testPrefix="landing-package-card" onSelect={(id) => navigate(`/register?package=${id}`)} />
        </section>
      )}
    </div>
  );
}
